import { Component, Input, OnDestroy, OnChanges, SimpleChanges, SimpleChange, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { EventListDto, EventDto } from 'UvExplorerServer.Dtos.TS/Events';
import { EventsService } from '../../services/events.service';
import { MessageService } from 'base';
import { AgentDto } from 'UvExplorerServer.Dtos.TS/Agents';
import { NetViewDto } from 'UvExplorerServer.Dtos.TS/NetViews';
import { ScheduledDiscovery } from 'DiscoveryEngine.TS/Discovery';
import { ScheduledDiscoveryResultDto } from 'UvExplorerServer.Dtos.TS/Discovery';
import { NetFacadeService, NavigationService } from 'netview';
import { DateService } from 'base';
import { DiscResultsService } from 'discovery';
import { Subscription } from 'rxjs';


export class EventEntry {
	public dataKey: string;
	public created: string;
	public category: string;
	public source: string;
	public level: string;
	public description: string;

	public createdTag: Date;
	public get hasCreatedTag(): boolean {
		return !!this.createdTag;
	}

	public eventTag: EventDto;
	public get hasEventTag(): boolean {
		return !!this.eventTag;
	}

	constructor() {
		this.dataKey = "";
		this.created = "";
		this.category = "";
		this.source = "";
		this.level = "";
		this.description = "";

		this.createdTag = null;
		this.eventTag = null;
	}
}


@Component({
	selector: 'events',
	templateUrl: './events.component.html',
	styleUrls: ['./events.component.scss']
})
export class EventsComponent implements OnDestroy, OnChanges {

	@Input()
	public network: NetViewDto;

	@Input()
	public agent: AgentDto;

	@Input()
	public discovery: ScheduledDiscovery;

	@Input()
	public discResult: ScheduledDiscoveryResultDto;

	@Input()
	public selected: boolean;

	public cols: any[];
	public rows: number;
	private events: EventListDto;
	public entries: EventEntry[];
	public totalCount: number;

	private sortBy: string;
	private ascending: boolean;
	public skip: number;
	private limit: number;

	private destroyed: boolean;
	private eventsInterval: number;
	private timerId: any;

	private categoryFilter: string;
	private sourceFilter: string;
	private levelFilter: string;
	private descriptionFilter: string;

	private onNetworkLoadedSubscription: Subscription;

	constructor(private changeDetector: ChangeDetectorRef,
		private eventsService: EventsService,
		private messageService: MessageService,
		private netFacadeService: NetFacadeService,
		private discResultsService: DiscResultsService,
		private navService: NavigationService,
		private dateService: DateService) {

		this.network = null;
		this.agent = null;
		this.discovery = null;
		this.discResult = null;
		this.selected = false;
		this.cols = [
			{ field: 'created', header: 'Time', sortable: "custom", style: "", sortFunction: (event) => this.doCustomSort(event) },
			{ field: 'category', header: 'Category', sortable: "custom", style: { width: '10%' }, sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
			{ field: 'source', header: 'Source', sortable: "custom", style: "", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
			{ field: 'level', header: 'Level', sortable: "custom", style: { width: '10%' }, sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
			{ field: 'description', header: 'Description', sortable: "custom", style: { width: '40%' }, sortFunction: (event) => this.doCustomSort(event), createTooltip: true, filterMatchMode: "contains" },
		];
		this.rows = 30;
		this.events = new EventListDto();
		this.sortBy = "created";
		this.ascending = false;
		this.limit = this.rows;
		this.destroyed = false;
		this.timerId = undefined;
		this.categoryFilter = "";
		this.sourceFilter = "";
		this.levelFilter = "";
		this.descriptionFilter = "";
		this.resetEvents();
	}

	private get shouldGetEvents(): boolean {
		if (this.selected && this.navService.isNetworkLoaded) {
			if (!!this.network || !!this.agent || !!this.discovery || !!this.discResult) {
				return true;
			}
		}
		return false;
	}

	private resetEvents(): void {
		this.entries = [];
		this.totalCount = 0;
		this.eventsInterval = 0;
		this.skip = 0;
	}

	private getEventsIfNecessary(): void {
		this.stopTimer();
		if (this.shouldGetEvents) {
			this.getEvents(true);
			//this.startTimer();
		}
	}

	public ngOnInit(): void {	
		this.onNetworkLoadedSubscription = this.discResultsService.onNetworkLoaded.subscribe(() => {
			this.resetEvents();
			this.getEventsIfNecessary();
		});
	}

	public ngOnDestroy(): void {
		this.destroyed = true;
		this.stopTimer();

		if (this.onNetworkLoadedSubscription) {
			this.onNetworkLoadedSubscription.unsubscribe();
			this.onNetworkLoadedSubscription = undefined;
		}
	}

	public ngOnChanges(changes: SimpleChanges): void {
		if (this.destroyed)
			return;

		if (changes["network"] || changes["agent"] || changes["discovery"] || changes["discResult"]) {
			this.resetEvents();
			this.getEventsIfNecessary();
		}
		else if (changes["selected"]) {
			this.getEventsIfNecessary();
		}
	}

	public filterUpdated(column: string, value: string, filterMatchMode: string): void {
		this[column + 'Filter'] = value;
	}

	private setEventsInterval(): void {
		this.eventsInterval = 0;
		if (this.network) {
			this.eventsInterval = this.network.EventsInterval;
		}
		else if (this.agent) {
			let network: NetViewDto = this.netFacadeService.findNetwork(this.agent.ScopeGuid);
			if (network) {
				this.eventsInterval = network.EventsInterval;
			}
		}
		else if (this.discovery) {
			let agent: AgentDto = this.netFacadeService.findSchedDiscParent(this.discovery.GUID);
			if (agent) {
				let network: NetViewDto = this.netFacadeService.findNetwork(agent.ScopeGuid);
				if (network) {
					this.eventsInterval = network.EventsInterval;
				}
			}
		}
		else if (this.discResult) {
			let network: NetViewDto = this.netFacadeService.findNetwork(this.discResult.ScopeGuid);
			if (network) {
				this.eventsInterval = network.EventsInterval;
			}
		}
	}

	private startTimer(): void {

		this.setEventsInterval();

		if (!this.timerId && this.selected && this.eventsInterval > 0) {
			let timeoutMillis = this.eventsInterval * 1000;
			this.timerId = setInterval(() => {
				if (this.shouldGetEvents) {
					this.getEvents(false);
				}
			}, timeoutMillis);
		}
	}

	private stopTimer(): void {
		if (this.timerId) {
			clearInterval(this.timerId);
			this.timerId = undefined;
		}
	}

	public doCustomSort(event): void {
		let comparer = function (a: EventEntry, b: EventEntry): number {
			let result = 0;

			switch (event.field) {
				case "created":
					if (a.createdTag && b.createdTag) {
						let aTicks = a.createdTag.getTime();
						let bTicks = b.createdTag.getTime();
						result = 0;
						if (aTicks < bTicks) {
							result = -1;
						}
						else if (aTicks > bTicks) {
							result = 1;
						}
					}
					else if (a.createdTag) {
						result = 1;
					}
					else if (b.createdTag) {
						result = -1;
					}
					break;
				default:
					// Compare string fields
					result = a[event.field].localeCompare(b[event.field]);
					break;
			}

			return result * event.order;
		}

		this.entries.sort(comparer);
	}

	public loadData(event: any): void {
		this.sortBy = event.sortField;
		this.ascending = (event.sortOrder == 1);
		this.skip = event.first;
		this.limit = event.rows;

		if (this.shouldGetEvents) {
			this.getEvents(false);
		}
	}

	private getEvents(showBusy: boolean): void {

		let scopeGuid = "";
		let agentGuid = "";
		let schedDiscGuid = "";
		let schedDiscResultGuid = "";

		scopeGuid = this.navService.location.netViewGuid;

		if (this.network) {
			console.assert(scopeGuid == this.network.Guid);
		}
		else if (this.agent) {
			agentGuid = this.agent.Guid;
		}
		else if (this.discovery) {
			schedDiscGuid = this.discovery.GUID;
		}
		else if (this.discResult) {
			schedDiscResultGuid = this.discResult.Guid;
		}
		else {
			return;
		}

		this.eventsService.getEvents(scopeGuid, agentGuid, "", schedDiscGuid, schedDiscResultGuid,
			this.categoryFilter, this.sourceFilter, this.levelFilter, this.descriptionFilter,
			this.sortBy, this.ascending, this.skip, this.limit, showBusy).subscribe(
				(result: EventListDto) => {
					this.events = result;
					this.rebuildEntries();
				},
				(error?: any) => {
					this.events = new EventListDto();
					this.rebuildEntries();
					this.messageService.displayErrorMessage("Could not retrieve events");
				});
	}

	private rebuildEntries(): void {

		this.changeDetector.markForCheck();

		this.entries = [];
		this.totalCount = this.events.TotalCount;

		this.events.Entries.ForEach((event: EventDto) => {

			let entry = new EventEntry();

			entry.eventTag = event;

			entry.createdTag = new Date(event.Created);
			entry.created = this.dateService.formatDateHm(event.Created);

			entry.category = event.Category;
			entry.source = event.Source;
			entry.level = event.Level;
			entry.description = event.Description;

			entry.dataKey = event.Guid;

			this.entries.push(entry);

		}, this);
	}

	public updateEvents(): void {
		if (this.shouldGetEvents) {
			this.skip = 0;
			this.getEvents(true);
		}
	}

}
